<template>
  <div class="bill-page">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo"></Loader>
    <KTCodePreview v-bind:title="getByType('title')">
      <template v-slot:toolbar>
        <b-row>
          <b-dropdown size="sm" id="dropdown-1" right>
            <template slot="button-content">
              <i style="font-size: 1rem" class="fas fa-cog"></i>
              <span class="font-weight-bolder">Thao tác</span>
            </template>
            <b-dropdown-item @click="showModalExcel">
              <span>
                <i style="font-size: 1rem" class="far fa-file-excel"></i>
                &nbsp; Xuất Excel</span
              >
            </b-dropdown-item>
          </b-dropdown>
          <div v-if="!isDeliveryBillViewOnly" class="ml-3">
            <router-link
              :to="{
                name: getByType('router'),
              }"
            >
              <b-button variant="primary" size="sm" class="font-weight-bolder">
                <i style="font-size: 1rem" class="flaticon2-add-1"></i> Tạo mới
              </b-button>
            </router-link>
          </div>
        </b-row>
      </template>
      <template v-slot:preview>
        <BillFilter
          :bill-type="billType"
          @filter="onClickFilter"
          @update-filter="updateFilter"
        />
        <div class="bill-list-toolbar">
          <div class="bill-list-toolbar__left">
            <span class="bill-list-toolbar__total">
              <span class="bill-list-toolbar__total-label">Tổng số:</span>
              <span class="bill-list-toolbar__total-value">{{ convertPrice(totalItems) }}</span>
            </span>
          </div>
          <div class="bill-list-toolbar__right">
            <BillRowsPerPageSelector
              v-model="rowsPerPage"
              :disabled="onLoading"
              @change="onRowsPerPageChange"
            />
            <div v-show="totalPages >= 2" class="bill-list-toolbar__divider"></div>
            <b-pagination-nav
              v-show="totalPages >= 2"
              pills
              size="sm"
              :number-of-pages="totalPages || 1"
              :link-gen="linkGen"
              align="right"
              v-model="page"
              use-router
              @change="fetchListBill()"
              class="bill-list-pagination"
            ></b-pagination-nav>
          </div>
        </div>
        <div
          :class="[
            'list-bill',
            { 'list-bill--wholesale': billType === BILL_TYPE.WHOLESALE_INVOICE },
          ]"
        >
          <table class="table table-bordered-list table-hover col-md-12">
            <thead>
              <tr>
                <th class="text-center tdCode">
                  <div class="d-flex align-items-center justify-content-center">
                    <b-form-checkbox
                      v-if="canCreateEInvoiceDraft"
                      :checked="isAllPageSelected"
                      :indeterminate="isIndeterminate"
                      @change="onToggleSelectAll"
                      class="bill-select-all-cb mr-2"
                      v-b-tooltip.hover
                      title="Chọn tất cả trên trang này"
                      aria-label="Chọn tất cả hóa đơn trên trang"
                    />
                    ID
                  </div>
                </th>
                <th v-if="billType !== BILL_TYPE.WHOLESALE_INVOICE" class="text-center tdCustomer">Khách hàng</th>
                <th :class="['text-center', 'tdProductName', { wholesale: billType === BILL_TYPE.WHOLESALE_INVOICE }]">Sản phẩm</th>
                <th class="text-center tdProductPrice">Giá</th>
                <th class="text-center tdQuantity">SL</th>
                <th class="text-center tdDiscountItem">C.Khấu</th>
                <th class="text-center tdTotalAmount">Tổng tiền</th>
                <th class="text-center tdTotalPayment">Thanh toán</th>
                <th :class="['text-center', 'tdNote', { wholesale: billType === BILL_TYPE.WHOLESALE_INVOICE }]">Ghi chú</th>
                <th
                  :class="['tdAction', { wholesale: billType === BILL_TYPE.WHOLESALE_INVOICE }]"
                ></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(bill, i) in bills">
                <tr
                  :key="billRowKey(bill, i, 'header')"
                  @mouseover="hoverRow"
                  @mouseleave="outRow"
                  :data="`row_${bill.billNumber}`"
                  :class="{ 'bill-row-selected': isBillSelected(bill.id) }"
                >
                  <td
                    :colspan="billType === BILL_TYPE.WHOLESALE_INVOICE ? 6 : 7"
                    style="
                      padding: 5px !important;
                      border-bottom: none !important;
                    "
                  >
                    <div class="d-flex align-items-start">
                      <b-form-checkbox
                        v-if="canCreateEInvoiceDraft"
                        :checked="isBillSelected(bill.id)"
                        @change="onToggleBillSelect(bill.id)"
                        class="mr-2 mt-1"
                        :aria-label="`Chọn hóa đơn ${bill.id}`"
                      />
                      <div class="flex-grow-1">
                        <div class="customer-header-info">
                      <small class="text-muted">Chi nhánh:</small>
                      <span>
                        <b>{{ bill.storeName }}</b>
                        <span
                          class="ml-1 text-muted"
                          v-if="bill.companyName"
                          style="font-size: 10px"
                          >( {{ bill.companyName }} )</span
                        >
                      </span>
                      <EInvoiceStatusBadge
                        v-if="bill.einvoice || canCreateEInvoiceDraft"
                        class="ml-2"
                        :work-state="bill.einvoice?.workState"
                        :invoice-no="bill.einvoice?.invoiceNo"
                        :pattern="bill.einvoice?.pattern"
                        :serial="bill.einvoice?.serial"
                        :tax-status="bill.einvoice?.taxStatus"
                        :issued-at="bill.einvoice?.issuedAt"
                        :error-message="bill.einvoice?.errorMessage"
                        size="sm"
                        @click="handleEInvoiceClick(bill)"
                      />
                      <b-badge
                        v-if="isExchangeBill(bill)"
                        variant="info"
                        class="ml-2"
                      >
                        {{ bill.billModeName || 'Xuất đổi' }}
                      </b-badge>

                    </div>
                    <div
                      v-if="billType === BILL_TYPE.WHOLESALE_INVOICE"
                      class="customer-header-info"
                    >
                      <small class="text-muted">KH/CTY:</small>
                      <span class="customer-header-info__values">
                        <span
                          class="customer-name orderCode cursor-pointer"
                          @click="editUser(bill)"
                        >{{ bill.customerName }}</span>
                        <span
                          class="customer-mobile orderCode cursor-pointer"
                          @click="editUser(bill)"
                        >{{ bill.customerMobile }}</span>
                      </span>
                    </div>
                    <div class="d-flex bill-header-meta flex-wrap">
                      <template v-if="isExchangeBill(bill)">
                        <span class="mr-4">
                          <small class="text-muted">Loại HĐ:</small>
                          <b-badge variant="info" class="ml-1">
                            {{ bill.billModeName || 'Xuất đổi' }}
                          </b-badge>
                        </span>
                        <span
                          v-if="getRelatedBillNumber(bill)"
                          class="mr-4"
                        >
                          <small class="text-muted">HĐ gốc:</small>
                          <span
                            class="text-primary font-weight-bold orderCode"
                            style="cursor: pointer"
                            @click="linkToRelatedBill(bill)"
                          >
                            #{{ getRelatedBillNumber(bill) }}
                          </span>
                        </span>
                        <span
                          v-if="getExchangeReasonName(bill)"
                          class="mr-4"
                        >
                          <small class="text-muted">Lý do:</small>
                          {{ getExchangeReasonName(bill) }}
                        </span>
                        <span
                          v-if="bill.exchangeCreditAmount != null"
                          class="mr-4"
                        >
                          <small class="text-muted">Credit HĐ gốc:</small>
                          {{ convertPrice(bill.exchangeCreditAmount) }}
                        </span>
                        <span
                          v-if="bill.exchangeAmountDue != null && bill.exchangeAmountDue > 0"
                          class="mr-4"
                        >
                          <small class="text-muted">Khách cần trả:</small>
                          <span class="text-primary font-weight-bold">{{
                            convertPrice(bill.exchangeAmountDue)
                          }}</span>
                        </span>
                        <span
                          v-if="bill.exchangeRefundPending > 0"
                          class="mr-4"
                        >
                          <small class="text-muted">Chờ hoàn:</small>
                          <span class="text-warning">{{
                            convertPrice(bill.exchangeRefundPending)
                          }}</span>
                        </span>
                      </template>
                      <span class="mr-4"
                        ><small class="text-muted">Ngày tạo:</small>
                        <span class="text-primary">
                          {{ bill.createdAt }}</span
                        ></span
                      >
                      <template v-if="billType === BILL_TYPE.WHOLESALE_INVOICE">
                        <span class="mr-4">
                          <small class="text-muted">NV lập phiếu:</small>
                          <span v-if="bill.cashierName">
                            {{ bill.cashierName }}
                          </span>
                          <span v-else class="text-danger"> Chưa gán </span>
                        </span>
                        <span class="mr-4">
                          <small class="text-muted">NV kinh doanh:</small>
                          <span v-if="bill.saleName">
                            {{ bill.saleName }}
                          </span>
                          <span v-else class="text-danger"> Chưa gán </span>
                        </span>
                        <span class="mr-4">
                          <small class="text-muted">NV điều phối:</small>
                          <span v-if="bill.assistantName">
                            {{ bill.assistantName }}
                          </span>
                          <span v-else class="text-danger"> Chưa gán </span>
                        </span>
                        <span class="mr-4">
                          <small class="text-muted">NVGH:</small>
                          <span v-if="bill.shipperName">
                            {{ bill.shipperName }}
                          </span>
                          <span v-else class="text-danger"> Chưa gán </span>
                        </span>
                      </template>
                      <template v-else>
                        <span class="mr-4">
                          <small class="text-muted">TVBH:</small>
                          <span v-if="bill.saleName">
                            {{ bill.saleName }}
                          </span>
                          <span v-else class="text-danger"> Chưa được gán </span>
                        </span>
                        <span class="mr-4">
                          <small class="text-muted">NVTN:</small>
                          <span v-if="bill.cashierName">
                            {{ bill.cashierName }}
                          </span>
                          <span v-else class="text-danger"> Chưa được gán </span>
                        </span>
                        <span class="mr-4">
                          <small class="text-muted">NVKT:</small>
                          <span v-if="bill.technicalName">
                            {{ bill.technicalName }}
                          </span>
                          <span v-else class="text-danger"> Chưa được gán </span>
                        </span>
                      </template>
                      <span class="mr-4" v-if="bill.stockSlipId">
                        <span class="mr-1"
                          ><i
                            v-b-tooltip.hover
                            title="Đã liên kết phiếu thu cũ"
                            style="color: #50cd89"
                            class="fa fa-link"
                          ></i
                        ></span>
                      </span>
                      <span class="mr-4">
                        Tổng SL: <span class="text-primary font-weight-bold">{{ convertPrice(billTotalQuantity(bill)) }}</span>
                      </span>
                    </div>
                      </div>
                    </div>
                  </td>
                  <td style="border-bottom: none !important" colspan="2">
                    <b-row>
                      <b-col cols="10">
                        <div class="d-flex bill-header-meta">
                          <span v-if="bill.couponCode">
                            Mã coupon: {{ bill.couponCode }}
                          </span>
                        </div>
                      </b-col>
                      <b-col cols="2" class="text-right">
                        <i
                          class="fa fa-arrow-circle-o-down"
                          aria-hidden="true"
                        ></i>
                      </b-col>
                    </b-row>
                  </td>
                  <td
                    style="border-bottom: none !important"
                    class="text-center"
                  >
                    <!-- <i
                      class="fa fa-download text-primary cursor-pointer"
                      aria-hidden="true"
                      @click="openFileModal(bill)"
                      v-if="billType !== BILL_TYPE.CONVERT"
                    ></i> -->
                  </td>
                </tr>
                <tr :key="i + 10" v-if="bill.showDetail">
                  <td :colspan="billType === BILL_TYPE.WHOLESALE_INVOICE ? 9 : 10" style="border-bottom: none !important">
                    <b-col cols="3"></b-col>
                  </td>
                </tr>
                <template v-for="(billItem, j) in bill.displayListProduct">
                  <tr
                    v-if="j < limitListProduct"
                    :key="billItemRowKey(bill, billItem, i, j)"
                    @mouseover="hoverBill"
                    @mouseleave="outBill"
                    :data="`row_${bill.billNumber}`"
                    :class="{ 'bill-row-selected': isBillSelected(bill.id) }"
                  >
                    <td
                      :rowspan="
                        bill.displayListProduct.length > limitListProduct
                          ? limitListProduct
                          : bill.displayListProduct.length
                      "
                      v-if="j === 0 || bill.show"
                      class="tdTextAlign tdCode"
                    >
                      <div class="text-center mb-2">
                        <span
                          title="Từ app mobile"
                          v-b-tooltip
                          v-if="bill.deviceType === 2"
                        >
                          <i class="fas fa-mobile-alt text-dark ml-1"></i>
                        </span>
                        <span
                          title="Từ web"
                          v-b-tooltip
                          v-else-if="bill.deviceType === 1"
                        >
                          <i class="fas fa-globe-asia text-dark"></i>
                        </span>
                        <span title="Chưa xác định" v-b-tooltip v-else>
                          <i
                            class="fa fa-stop-circle text-danger"
                            aria-hidden="true"
                          ></i>
                        </span>
                      </div>
                      <div
                        class="orderCode"
                        style="cursor: pointer; color: #3699ff"
                      >
                        <a
                          class="font-weight-bold"
                          v-bind:href="getByType('convert', bill)"
                          target="_self"
                          v-if="billType === BILL_TYPE.CONVERT"
                        >
                          {{ bill.billNumber }}
                        </a>
                        <a
                          class="font-weight-bold"
                          v-bind:href="getByType('router-link-id', bill)"
                          target="_self"
                          v-else
                        >
                          {{ bill.billNumber }}
                        </a>
                      </div>
                      <div v-if="bill.type === 4" class="text-center">
                        <span class="text-success"
                          ><i
                            class="fas fa-shipping-fast"
                            title="Hóa đơn giao hàng từ đơn hàng"
                          ></i
                        ></span>
                        <div
                          class="orderCode"
                          @click="linkToOrder(bill.orderId)"
                          style="cursor: pointer"
                        >
                          <span
                            style="color: darkgoldenrod"
                            v-text="bill.orderId"
                          ></span>
                        </div>
                      </div>
                      <div
                        v-if="bill.type === 1 && bill.orderId"
                        class="text-center"
                      >
                        <span class="text-warning"
                          ><i
                            class="fas fa-shopping-basket"
                            title="Hóa đơn từ đơn hàng"
                          ></i
                        ></span>
                        <div
                          class="orderCode"
                          @click="linkToOrder(bill.orderId)"
                          style="cursor: pointer"
                        >
                          <span
                            style="color: darkgoldenrod"
                            v-text="bill.orderId"
                          ></span>
                        </div>
                      </div>
                      <div v-if="isExchangeBill(bill)" class="text-center mt-1">
                        <span
                          class="text-info"
                          v-b-tooltip
                          title="Hóa đơn xuất đổi"
                        >
                          <i class="fas fa-exchange-alt"></i>
                        </span>
                        <div class="small text-info font-weight-bold">
                          {{ bill.billModeName || 'Xuất đổi' }}
                        </div>
                        <div
                          v-if="getRelatedBillNumber(bill)"
                          class="orderCode"
                          @click="linkToRelatedBill(bill)"
                          style="cursor: pointer"
                        >
                          <span
                            style="color: darkgoldenrod"
                            v-text="'#' + getRelatedBillNumber(bill)"
                          ></span>
                        </div>
                      </div>
                    </td>
                    <td
                      :rowspan="
                        bill.displayListProduct.length > limitListProduct
                          ? limitListProduct
                          : bill.displayListProduct.length
                      "
                      v-if="(j === 0 || bill.show) && billType !== BILL_TYPE.WHOLESALE_INVOICE"
                      class="tdTextAlign tdCustomer"
                    >
                      <p
                        class="orderCode"
                        @click="editUser(bill)"
                        style="
                          margin-bottom: 0;
                          cursor: pointer;
                          font-weight: 550;
                        "
                      >
                        {{ bill.customerName }}
                      </p>
                      <p
                        class="orderCode"
                        @click="editUser(bill)"
                        style="margin-bottom: 0; cursor: pointer"
                      >
                        {{ bill.customerMobile }}
                      </p>
                    </td>
                    <td :class="['tdTextAlign', 'tdProductName', { wholesale: billType === BILL_TYPE.WHOLESALE_INVOICE }]">
                      <div v-if="billItem.typeProduct !== 3">
                        <div class="d-flex align-items-center flex-gap-8">
                          <!-- Product Image -->
                          <ProductImage
                            :src="billItem.imageUrl"
                            class="size-40 object-contain flex-shrink-0"
                          />

                          <!-- Product Information -->
                          <div class="product-info flex-grow-1 min-w-0">
                            <!-- Top: barCode + productCode -->
                            <div class="product-codes">
                              <span v-if="billItem.barCode" class="code-text">
                                {{ billItem.barCode }}
                              </span>
                              <br />
                              <span v-if="billItem.productCode" class="code-text">
                                ({{ billItem.productCode }})
                              </span>
                            </div>

                            <!-- Middle: product name + type icon -->
                            <div class="product-title">
                              <i
                                v-if="billItem.productType === 2"
                                class="fas fa-gift product-icon gift"
                                title="Quà tặng"
                              ></i>
                              <i
                                v-else-if="billItem.productType === 3"
                                class="fas fa-tags product-icon attach"
                                title="Bán kèm"
                              ></i>
                              <span class="product-name">{{
                                billItem.productName
                              }}</span>
                            </div>

                            <!-- Bán buôn: IMEI gom — chuỗi "a, b"; dài thì ... + tooltip -->
                            <div
                              v-if="
                                billType === BILL_TYPE.WHOLESALE_INVOICE &&
                                billItem.imeiCode &&
                                billItem.imeiCode.trim() !== ''
                              "
                              class="product-meta min-w-0"
                            >
                              <span
                                class="bill-list-imei-text text-muted"
                                v-b-tooltip.hover.bottom.noninteractive
                                :title="billListImeiTitleAttr(billItem.imeiCode)"
                              >
                                {{ billListImeiShort(billItem.imeiCode) }}
                              </span>
                            </div>
                            <!-- Bán lẻ / loại khác: giữ ImeiDisplay như cũ -->
                            <div
                              v-else-if="
                                billItem.imeiCode &&
                                billItem.imeiCode.trim() !== ''
                              "
                              class="product-meta"
                            >
                              <ImeiDisplay :imei="billItem.imeiCode" />
                            </div>
                            <div
                              v-if="
                                isExchangeBill(bill) &&
                                billItem.newProductId &&
                                billItem.newProductName
                              "
                              class="product-meta exchange-new-product small"
                            >
                              <i class="fas fa-exchange-alt text-info mr-1"></i>
                              <span class="text-muted">→</span>
                              {{ billItem.newProductName }}
                              <span
                                v-if="billItem.newImeiCode"
                                class="text-muted ml-1"
                              >
                                ({{ billItem.newImeiCode }})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <div
                          class="table-responsive"
                          v-show="
                            billItem.comboItems !== undefined &&
                            billItem.comboItems.length > 0
                          "
                        >
                          <table>
                            <tr>
                              <td style="border: none; padding-left: 0">
                                <p class="m-0">
                                  {{ billItem.productName }}
                                  <!-- <MerchantLabel
                                    :merchantId="billItem.merchantId"
                                  /> -->
                                </p>
                              </td>
                            </tr>
                            <tr
                              v-for="(items, i) in billItem.comboItems"
                              :key="i"
                              style="border-top: 1px solid gray"
                            >
                              <td
                                style="
                                  border: none;
                                  width: -webkit-fill-available;
                                "
                              >
                                <i
                                  style="color: rgb(24, 28, 50)"
                                  class="fas fa-cube"
                                ></i>
                                {{ items.productName }}
                                <!-- <MerchantLabel
                                  :merchantId="billItem.merchantId"
                                /> -->
                                <i style="font-size: unset; color: unset"
                                  >({{ convertPrice(items.sellingPrice) }})</i
                                >
                              </td>
                              <td style="border: none; font-size: 13px">
                                {{ items.quantity }}
                              </td>
                            </tr>
                          </table>
                        </div>
                      </div>
                    </td>
                    <td class="tdTextAlignPrice tdProductPrice">
                      {{ convertPrice(billItem.productPrice) }}
                    </td>
                    <td class="tdTextAlignPrice tdQuantity">
                      {{ billItem.quantity }}
                    </td>
                    <td class="tdTextAlignPrice tdDiscountItem">
                      <p v-if="billItem.discountType === 1">
                        {{ convertPrice(billItem.discount) }}
                      </p>
                      <p
                        v-if="
                          billItem.discountType === 2 && billItem.discount > 0
                        "
                      >
                        {{ convertPrice(billItem.discount) }}%
                        <br />
                        <span style="color: #ffa800; font-style: italic">
                          {{ convertPrice(billItem.discountItemAmount) }}</span
                        >
                      </p>
                      <p
                        v-if="
                          billItem.discountType === 2 && billItem.discount === 0
                        "
                      >
                        {{ convertPrice(billItem.discount) }}
                      </p>
                    </td>
                    <td
                      class="tdTextAlignPrice tdTotalAmount"
                      :rowspan="
                        bill.displayListProduct.length > limitListProduct
                          ? limitListProduct
                          : bill.displayListProduct.length
                      "
                      v-if="j === 0 || bill.show"
                    >
                      <p v-show="bill.shippingFee > 0" class="border-0">
                        <i
                          v-b-tooltip.hover
                          title="Phí ship"
                          class="fas fa-shipping-fast icon-color"
                        ></i>
                        : {{ convertPrice(bill.shippingFee) }}
                      </p>
                      <p
                        v-show="
                          bill.discountAmount > 0 ||
                          bill.discountAmount.includes('%') ||
                          bill.discountAmount.includes('.')
                        "
                        class="border-0 text-warning"
                      >
                        <i
                          v-b-tooltip.hover
                          title="Chiết khấu tổng"
                          class="fa fa-arrow-down icon-color"
                        ></i>
                        : {{ bill.discountAmount }}
                      </p>
                      <b style="color: #3c763d">
                        <template v-if="isExchangeBill(bill) && bill.exchangeAmountDue != null">
                          {{ convertPrice(bill.exchangeAmountDue) }}
                          <small
                            v-if="bill.exchangeCreditAmount > 0"
                            class="text-muted d-block"
                            style="font-weight: normal; font-size: 0.75rem"
                          >
                            (Credit: {{ convertPrice(bill.exchangeCreditAmount) }})
                          </small>
                        </template>
                        <template v-else>
                          {{ convertPrice(bill.totalAmount) }}
                        </template>
                      </b>
                    </td>
                    <td
                      class="tdTextAlignPrice tdTotalPayment"
                      :rowspan="
                        bill.displayListProduct.length > limitListProduct
                          ? limitListProduct
                          : bill.displayListProduct.length
                      "
                      v-if="j === 0 || bill.show"
                    >
                      <p v-show="bill.pointToMoney > 0" class="border-0">
                        <i
                          v-b-tooltip.hover
                          title="Tiêu điểm"
                          class="fas fa-adjust icon-color"
                        ></i>
                        : {{ convertPrice(bill.pointToMoney) }}
                      </p>
                      <template v-if="bill.payments && bill.payments.length">
                        <div
                          v-for="(payment, index) in bill.payments"
                          :key="index"
                          class="mb-2"
                        >
                          <span class="mr-2">
                            <i
                              v-b-tooltip.hover
                              :title="payment.paymentName"
                              class="icon-color text-dark mr-1"
                              :class="
                                payment.paymentType === 1
                                  ? 'fas fa-money-bill-alt'
                                  : payment.paymentType === 2
                                    ? 'fa fa-university'
                                    : payment.paymentType === 3
                                      ? 'fa fa-credit-card'
                                      : payment.paymentType === 4
                                        ? 'fa fa-cube'
                                        : ''
                              "
                            ></i
                            >:
                          </span>
                          <span style="font-weight: 700">{{
                            convertPrice(payment.paymentAmount)
                          }}</span>
                        </div>
                      </template>
                      <p v-show="bill.conversionFee > 0" class="border-0">
                        <i
                          v-b-tooltip.hover
                          title="Thanh toán phí chuyển đổi"
                          class="fa-solid fa-money-bill-transfer icon-color text-dark"
                        ></i>
                        : {{ convertPrice(bill.conversionFee) }}
                      </p>
                      <p
                        v-if="getBillDisplayExtraAmount(bill) > 0"
                        class="d-block text-primary"
                      >
                        Tiền thừa:
                        <span class="d-block">{{
                          convertPrice(getBillDisplayExtraAmount(bill))
                        }}</span>
                      </p>
                      <p
                        v-if="getBillDisplayExtraAmount(bill) < 0"
                        class="d-block"
                        style="color: #e65b5b"
                      >
                        Còn thiếu:
                        <span class="d-block">
                          {{
                            convertPrice(getBillDisplayExtraAmount(bill) * -1)
                          }}</span
                        >
                      </p>
                      <p v-show="getBillDisplayLeftAmount(bill) > 0">
                        Công nợ:
                        <span class="d-block">
                          {{ convertPrice(getBillDisplayLeftAmount(bill)) }}</span
                        >
                      </p>
                    </td>
                    <td
                      :class="[
                        'tdTextAlign',
                        'tdNote',
                        'wrapword',
                        { wholesale: billType === BILL_TYPE.WHOLESALE_INVOICE },
                      ]"
                      :rowspan="
                        bill.displayListProduct.length > limitListProduct
                          ? limitListProduct
                          : bill.displayListProduct.length
                      "
                      v-if="j === 0 || bill.show"
                      @mouseover="hoverPrice = true"
                      @mouseleave="hoverPrice = false"
                    >
                      <span
                        v-if="bill.orderCodeShip && bill.orderCodeShip.trim().length > 0"
                        class="d-block mb-1"
                      >
                        <b>Mã vận đơn:</b> {{ bill.orderCodeShip }}
                      </span>
                      <span
                        v-if="bill.orderExternalId && bill.orderExternalId.trim().length > 0"
                        class="d-block mb-1"
                      >
                        <b>Mã đơn sàn:</b> {{ bill.orderExternalId }}
                      </span>
                      <p
                        v-if="
                          ((bill.orderCodeShip && bill.orderCodeShip.trim()) ||
                            (bill.orderExternalId && bill.orderExternalId.trim())) &&
                          (bill.customerNote || bill.warrantyNote)
                        "
                        class="dashed"
                      ></p>
                      <p
                        v-if="bill.customerNote"
                        :title="bill.customerNote"
                        v-b-tooltip.hover
                      >
                        {{ truncateText(bill.customerNote) }}
                      </p>
                      <p
                        v-if="bill.customerNote && bill.warrantyNote"
                        class="dashed"
                      ></p>
                      <p
                        v-if="bill.warrantyNote"
                        :title="bill.warrantyNote"
                        v-b-tooltip.hover
                      >
                        {{ truncateText(bill.warrantyNote) }}
                      </p>
                      <span
                        v-if="!isDeliveryBillViewOnly"
                        @click="handleShowNoteModel(bill)"
                      >
                        <i class="fas fa-edit cursor-pointer"></i>
                      </span>
                    </td>
                    <td
                      :rowspan="
                        bill.displayListProduct.length > limitListProduct
                          ? limitListProduct
                          : bill.displayListProduct.length
                      "
                      v-if="j === 0 || bill.show"
                      :class="[
                        'tdTextAlign',
                        'tdAction',
                        { wholesale: billType === BILL_TYPE.WHOLESALE_INVOICE },
                      ]"
                    >
                      <div
                        class="d-flex justify-content-center"
                        v-if="billType !== BILL_TYPE.CONVERT"
                      >
                        <template v-if="isDeliveryBillViewOnly">
                          <b-button
                            variant="outline-primary"
                            size="sm"
                            class="py-1 px-2 mr-1"
                            @click="printBill(bill)"
                          >
                            <i class="flaticon2-printer"></i>
                            &nbsp; In hóa đơn
                          </b-button>
                        </template>
                        <b-dropdown
                          v-else
                          size="sm"
                          id="dropdown-left"
                          no-caret
                          right
                        >
                          <template slot="button-content">
                            <i class="flaticon2-settings function-btn"></i>
                          </template>
                          <b-dropdown-item
                            @click="printBienBanGiaoHang(bill)"
                            v-if="showPrintBillInvoice(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon2-printer"
                              ></i>
                              &nbsp; In biên bản giao hàng hóa
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click="printHopDongMuaBan(bill)"
                            v-if="showPrintBillInvoice(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon2-printer"
                              ></i>
                              &nbsp; In hợp đồng mua bán
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click="printPhuLucImei(bill)"
                            v-if="showPrintBillInvoice(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon2-printer"
                              ></i>
                              &nbsp; In phụ lục danh sách IMEI
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item @click="printBill(bill)">
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon2-printer"
                              ></i>
                              &nbsp; In hóa đơn
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            v-if="showPrintBillInvoice(bill)"
                            @click="exportDanhSachImeiExcel(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="far fa-file-excel"
                              ></i>
                              &nbsp; Xuất excel danh sách IMEI
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click="refundBill(bill)"
                            v-if="bill.isShowReturnBill && !isExchangeBill(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon2-reply-1"
                              ></i>
                              &nbsp; Trả hàng
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click="exchangeBill(bill)"
                            v-if="bill.isShowReturnBill && !isExchangeBill(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="fas fa-exchange-alt"
                              ></i>
                              &nbsp; Xuất đổi
                            </span>
                          </b-dropdown-item>
                          <!-- create warranty bill button -->
                          <b-dropdown-item
                            @click="showSelectProductWarranty(bill)"
                            v-if="shouldShowCreateWarrantyReception(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="fas fa-toolbox"
                              ></i>
                              &nbsp; Tạo phiếu tiếp nhận bảo hành
                            </span>
                          </b-dropdown-item>
                          <!-- <b-dropdown-item
                            @click="showPopupVatInvoice(bill, 'add')"
                            v-if="isShowByType('invoice-vat')"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="fas fa-exchange-alt"
                              ></i>
                              &nbsp; Tạo hóa đơn đỏ
                            </span>
                          </b-dropdown-item> -->
                          <b-dropdown-item
                            @click="
                              showApprovalModal(
                                bill,
                                APPROVAL_TYPE.OVER_PAYMENT,
                              )
                            "
                            v-if="
                              isShowApprovalModal(
                                getBillDisplayExtraAmount(bill),
                              )
                            "
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="fas fa-hand-holding-usd"
                              ></i>
                              &nbsp; Hoàn tiền dư
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click="showApprovalModal(bill, APPROVAL_TYPE.VAT)"
                            v-if="
                              isShowApprovalModal(
                                getBillDisplayExtraAmount(bill),
                              )
                            "
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="fas fa-file-invoice"
                              ></i>
                              &nbsp; Hoàn tiền xuất VAT
                            </span>
                          </b-dropdown-item>
                          <!-- <b-dropdown-item
                            @click="editItem(bill)"
                            v-if="checkViewOnly() || bill.companyId !== 3"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon-eye"
                              ></i>
                              &nbsp; Chi tiết hóa đơn
                            </span>
                          </b-dropdown-item> -->
                          <b-dropdown-item @click="editItem(bill)">
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem"
                                class="flaticon2-pen"
                              ></i>
                              &nbsp; Chỉnh sửa
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            v-if="canDeleteBill && isExchangeBill(bill)"
                            @click="showReverseExchangeAlert(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem; color: #d33"
                                class="fas fa-undo"
                              ></i>
                              &nbsp; Hoàn xuất đổi
                            </span>
                          </b-dropdown-item>
                          <b-dropdown-item
                            v-if="canDeleteBill && !isExchangeBill(bill)"
                            @click="showDeleteAlert(bill)"
                          >
                            <span style="color: #3f4254; font-size: 12px">
                              <i
                                style="font-size: 1rem; color: #d33"
                                class="flaticon2-rubbish-bin-delete-button"
                              ></i>
                              &nbsp; Xóa
                            </span>
                          </b-dropdown-item>
                        </b-dropdown>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
            <tfoot>
              <tr v-if="!onLoading">
                <td :colspan="billType === BILL_TYPE.WHOLESALE_INVOICE ? 3 : 4" class="tdTextAlignPrice text-weight">Tổng</td>
                <td class="tdTextAlignPrice text-weight">
                  {{ convertPrice(totalQuantity) }}
                </td>
                <td></td>
                <td class="tdTextAlignPrice text-weight">
                  {{ convertPrice(totalAmount) }}
                </td>
                <td class="tdTextAlignPrice text-weight">
                  {{ convertPrice(totalPayment) }}
                </td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <vcl-table
            v-if="onLoading"
            :speed="5"
            :animate="true"
            :columns="11"
          ></vcl-table>
          <div class="bill-list-toolbar">
            <div class="bill-list-toolbar__left">
              <span class="bill-list-toolbar__total">
                <span class="bill-list-toolbar__total-label">Tổng số:</span>
                <span class="bill-list-toolbar__total-value">{{ convertPrice(totalItems) }}</span>
              </span>
            </div>
            <div class="bill-list-toolbar__right">
              <BillRowsPerPageSelector
                v-model="rowsPerPage"
                :disabled="onLoading"
                @change="onRowsPerPageChange"
              />
              <div v-show="totalPages >= 2" class="bill-list-toolbar__divider"></div>
              <b-pagination-nav
                v-show="totalPages >= 2"
                pills
                size="sm"
                :number-of-pages="totalPages || 1"
                :link-gen="linkGen"
                align="right"
                v-model="page"
                use-router
                @change="fetchListBill()"
                class="bill-list-pagination"
              ></b-pagination-nav>
            </div>
          </div>
          <template>
            <b-modal
              v-model="showEditNoteModal"
              hide-footer
              :titleHtml="titleNoteModal"
            >
              <b-row class="mb-5">
                <b-col>
                  <label class="labelInput" for="inputOriginalPrice"
                    >Ghi chú bán hàng</label
                  >
                  <b-form-textarea
                    v-model="customerNote"
                    placeholder="Nhập ghi chú..."
                    rows="5"
                    max-rows="6"
                  ></b-form-textarea>
                </b-col>
              </b-row>
              <b-row class="mb-5">
                <b-col>
                  <label class="labelInput" for="inputOriginalPrice"
                    >Ghi chú bảo hành</label
                  >
                  <b-form-textarea
                    v-model="warrantyNote"
                    placeholder="Nhập ghi chú..."
                    rows="5"
                    max-rows="6"
                  ></b-form-textarea>
                </b-col>
              </b-row>
              <b-row d-flex justify-content-end>
                <b-col>
                  <b-button
                    class="edit-note-btn"
                    variant="primary"
                    size="sm"
                    @click="updateNote"
                    >Lưu
                  </b-button>
                  <b-button
                    variant="secondary"
                    class="ml-2"
                    size="sm"
                    @click="handleShowNoteModel"
                    >Hủy
                  </b-button>
                </b-col>
              </b-row>
            </b-modal>
          </template>
          <template>
            <BillExcel :param="apiParams" :mode="billType" />
          </template>
        </div>
      </template>
    </KTCodePreview>

    <ApprovalModal ref="popup-approval" />
    <template>
      <BillListSelectWarrantyItemSideBar :value="showSideBar" />
    </template>

    <EInvoiceCreateDraftModal
      ref="batchCreateDraftModal"
      @finished="onBatchDraftFinished"
    />
    <transition name="batch-bar-slide">
      <div
        v-if="canCreateEInvoiceDraft && selectedBills.length > 0"
        class="batch-action-bar"
        role="toolbar"
        aria-label="Thao tác hàng loạt"
      >
        <div class="batch-bar-inner">
          <!-- Left: Selection count + warning if partial -->
          <div class="batch-bar-left">
            <span class="batch-bar-count-text">
              Đã chọn <strong>{{ selectedBills.length }}</strong> hóa đơn
            </span>
            <span
              v-if="hasIneligibleSelection && !batchFacetLoading"
              class="batch-bar-warning"
              v-b-tooltip.hover.top
              :title="ineligibleWarningTooltip"
            >
              <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            </span>
          </div>

          <!-- Right: Actions -->
          <div class="batch-bar-right">
            <button
              type="button"
              class="batch-bar-btn batch-bar-btn--clear"
              @click="clearSelection"
              :disabled="batchFacetLoading"
            >
              Bỏ chọn tất cả
            </button>

            <button
              type="button"
              class="batch-bar-btn batch-bar-btn--primary"
              @click="openBatchCreateDraftModal"
              :disabled="isBatchPrimaryDisabled"
              :title="batchPrimaryDisabledReason"
              :aria-busy="batchFacetLoading"
            >
              <span v-if="batchFacetLoading" class="batch-bar-spinner" aria-hidden="true"></span>
              <i v-else class="fas fa-file-invoice" aria-hidden="true"></i>
              <span>{{ batchPrimaryActionLabel }}</span>
              <span v-if="showBatchActionCount" class="batch-bar-count-badge">
                ({{ batchActionableCount }})
              </span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import Loader from '@/view/content/Loader.vue';
import Swal from 'sweetalert2';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { BILL_TYPE, APPROVAL_TYPE_ENUM, BILL_MODE } from '@/utils/enum';
import { EXCHANGE_REASON_NAME } from '@/utils/enum-name';
import jQuery from 'jquery';
import ApiService from '@/core/services/api.service';
import { mapActions, mapGetters } from 'vuex';
import BillFilter from '@/view/components/bills/BillFilter.vue';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { validateBillProductSearchInput } from '@/utils/productSearch';
import { convertPrice, truncateText, handleError } from '@/utils/common';
import { VclTable } from 'vue-content-loading';
import _sumBy from 'lodash/sumBy';
import _flatten from 'lodash/flatten';
import moment from 'moment';
import BillExcel from '@/view/components/bills/BillExcel.vue';
import fileDownload from '@/utils/file-download';
import ApprovalModal from '@/view/components/approval/ApprovalModal.vue';
import ImeiDisplay from '@/view/components/imeis/ImeiDisplay.vue';
import ProductImage from '@/view/components/products/ProductImage.vue';
import BillListSelectWarrantyItemSideBar from '@/view/components/bills/BillListSelectWarrantyItemSideBar.vue';
import {
  groupWholesaleBillListProducts,
  imeiListShortLabel,
} from '@/view/components/bills/helpers/wholesaleImeiBillTransform';
import EInvoiceCreateDraftModal from '@/view/pages/einvoice/components/EInvoiceCreateDraftModal.vue';
import EInvoiceStatusBadge from '@/view/components/bills/EInvoiceStatusBadge.vue';
import BillRowsPerPageSelector from '@/view/components/bills/BillRowsPerPageSelector.vue';
import { EINVOICE_FETCH_FACETS_BATCH } from '@/core/services/store/modules/einvoice/einvoice.module'
import { canBootstrapDraftFromFacet as isFacetBootstrapEligible } from '@/core/services/store/modules/einvoice/einvoice.adapter'
import {
  getCanonicalSaleTypeByBillType,
  requireEInvoiceLaneByBillType,
} from '@/view/pages/bills/einvoice-sale-type.utils'
import { buildInvoiceJourneyUiMeta } from '@/view/pages/einvoice/utils/lifecycleState.utils'
import { getToastInstance } from '@/utils/toastHelper'

const { makeToastWarning } = getToastInstance()
import permissionMixin from '@/view/mixins/permission.mixin'

export default {
  mixins: [permissionMixin],
  props: {
    billType: {
      type: Number,
      required: true,
    },
  },
  watch: {
    selectedBills: {
      handler(val) {
        if (val.length) {
          this.refreshSelectedBillFacets()
        }
        this.updateBatchBarPadding()
      },
      deep: false,
    },
  },
  data() {
    return {
      BILL_TYPE,
      bills: [],
      totalPages: 0,
      totalItems: 0,
      onLoading: false,
      page: 1,
      hoverTable: {
        dataId: null,
      },
      hoverPrice: false,
      limitListProduct: 10,
      idBill: '',
      showEditNoteModal: false,
      customerNote: '',
      warrantyNote: '',
      titleNoteModal: '',
      apiParams: {},
      appliedFilterParams: {},
      APPROVAL_TYPE: APPROVAL_TYPE_ENUM,
      showSideBar: false,
      selectedBills: [],
      rowsPerPage: 10,
    };
  },
  components: {
    KTCodePreview,
    Loader,
    BillFilter,
    VclTable,
    BillExcel,
    ApprovalModal,
    ImeiDisplay,
    ProductImage,
    BillListSelectWarrantyItemSideBar,
    EInvoiceCreateDraftModal,
    EInvoiceStatusBadge,
    BillRowsPerPageSelector,
  },
  created() {},
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
    totalQuantity: {
      get() {
        const flattenBillItems = _flatten(
          this.bills.map((bill) => bill.listProduct),
        );
        return _sumBy(flattenBillItems, 'quantity');
      },
      set(amount) {
        return amount;
      },
    },
    totalAmount: {
      get() {
        return _sumBy(this.bills, 'totalAmount');
      },
      set(amount) {
        return amount;
      },
    },
    totalPayment: {
      get() {
        return _sumBy(this.bills, 'paymentAmount');
      },
      set(amount) {
        return amount;
      },
    },
    pageBillNumbers() {
      return this.bills.map((b) => b.billNumber)
    },
    pageBillIds() {
      return this.bills.map((b) => b.id)
    },
    isAllPageSelected() {
      return (
        this.pageBillIds.length > 0 &&
        this.pageBillIds.every((bn) => this.selectedBills.includes(bn))
      )
    },
    isIndeterminate() {
      const selected = this.pageBillIds.filter((bn) => this.selectedBills.includes(bn))
      return selected.length > 0 && selected.length < this.pageBillIds.length
    },
    batchSaleTypeLabel() {
      return this.billType === BILL_TYPE.WHOLESALE_INVOICE ? 'Bán buôn' : 'Bán lẻ'
    },
    batchSaleTypeIcon() {
      return this.billType === BILL_TYPE.WHOLESALE_INVOICE
        ? 'fas fa-building'
        : 'fas fa-cash-register'
    },
    einvoiceRouteSaleType() {
      return getCanonicalSaleTypeByBillType(this.billType)
    },
    selectedBillFacets() {
      return this.selectedBills.map((salesInvoiceId) =>
        this.$store.getters.einvoiceFacetBySalesInvoiceId(
          salesInvoiceId,
          this.einvoiceRouteSaleType,
        ),
      )
    },
    selectedDraftableBills() {
      return this.bills.filter((bill) => {
        if (!this.selectedBills.includes(bill.id)) return false
        const facet = this.$store.getters.einvoiceFacetBySalesInvoiceId(
          bill.id,
          this.einvoiceRouteSaleType,
        )
        return this.canBootstrapDraftFromFacet(facet)
      })
    },
    selectedOpenableBills() {
      return this.bills.filter((bill) => {
        if (!this.selectedBills.includes(bill.id)) return false
        const facet = this.$store.getters.einvoiceFacetBySalesInvoiceId(
          bill.id,
          this.einvoiceRouteSaleType,
        )
        return facet && !this.canBootstrapDraftFromFacet(facet)
      })
    },
    batchFacetLoading() {
      return this.selectedBills.some((salesInvoiceId) =>
        this.$store.getters.einvoiceFacetLoadingBySalesInvoiceId(
          salesInvoiceId,
          this.einvoiceRouteSaleType,
        ),
      )
    },
    hasIneligibleSelection() {
      if (this.batchFacetLoading) return false
      return this.selectedDraftableBills.length < this.selectedBills.length
    },
    ineligibleWarningTooltip() {
      const ineligible = this.selectedBills.length - this.selectedDraftableBills.length
      if (ineligible === 0) return ''
      return `${ineligible} hóa đơn không đủ điều kiện (đã có HĐĐT hoặc thiếu thông tin)`
    },
    isBatchPrimaryDisabled() {
      if (this.batchFacetLoading) return true
      return this.selectedDraftableBills.length === 0 && this.selectedOpenableBills.length === 0
    },
    batchPrimaryDisabledReason() {
      if (this.batchFacetLoading) return 'Đang kiểm tra trạng thái...'
      if (this.selectedDraftableBills.length === 0 && this.selectedOpenableBills.length === 0) {
        return 'Không có hóa đơn nào đủ điều kiện tạo nháp'
      }
      return ''
    },
    canDeleteBill() {
      if (this.billType === BILL_TYPE.WHOLESALE_INVOICE) {
        return this.checkPermission('BILL_WHOLESALE_DELETE')
      } else if (this.billType === BILL_TYPE.SHIPPING) {
        return this.checkPermission('BILL_DELIVERY_DELETE')
      }
      return this.checkPermission('BILL_DELETE')
    },
    batchPrimaryActionLabel() {
      if (this.batchFacetLoading) {
        return 'Đang kiểm tra...'
      }
      if (
        this.selectedBills.length === 1 &&
        this.selectedDraftableBills.length === 0 &&
        this.selectedOpenableBills.length === 1
      ) {
        const facet = this.$store.getters.einvoiceFacetBySalesInvoiceId(
          this.selectedOpenableBills[0].id,
        )
        return buildInvoiceJourneyUiMeta(facet || {}).label
      }
      if (this.selectedDraftableBills.length === 0) {
        return 'Không thể tạo nháp'
      }
      return 'Tạo hóa đơn nháp'
    },
    showBatchActionCount() {
      return !this.batchFacetLoading && this.selectedDraftableBills.length > 0
    },
    batchActionableCount() {
      return this.selectedDraftableBills.length
    },
    canCreateEInvoiceDraft() {
      // TODO(einvoice-permission): restore real permission checks after temporary UI review access.
      return true
    },
    /** Hóa đơn giao hàng: chỉ xem, không chỉnh sửa/xóa/ghi chú trên danh sách */
    isDeliveryBillViewOnly() {
      return this.billType === BILL_TYPE.SHIPPING;
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hóa đơn', route: 'bills' },
      { title: 'Danh sách hóa đơn' },
    ]);
  },
  methods: {
    ...mapActions(['fetchSalesReceiptForCheckWarranty']),
    convertPrice,
    truncateText,
    billTotalQuantity(bill) {
      return (bill.displayListProduct || []).reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0,
      )
    },
    billListImeiShort(imeiStr) {
      return imeiListShortLabel(imeiStr).short
    },
    /** Tooltip Bootstrap + title: chỉ khi chuỗi IMEI bị rút gọn (danh sách dài). */
    billListImeiTitleAttr(imeiStr) {
      const p = imeiListShortLabel(imeiStr)
      return p.truncated ? p.full : undefined
    },
    linkGen: (pageNum) => {
      return pageNum === 1 ? '?' : `?page=${pageNum}`;
    },
    getByType(name, item) {
      switch (name) {
        case 'router': {
          if (this.billType === BILL_TYPE.RETAIL_INVOICE) return 'add-bill';
          if (this.billType === BILL_TYPE.WHOLESALE_INVOICE)
            return 'add-wholesale-invoice';
          if (this.billType === BILL_TYPE.SHIPPING) return null;
          return null;
        }
        case 'router-update': {
          if (this.billType === BILL_TYPE.RETAIL_INVOICE) return 'update-bill';
          if (this.billType === BILL_TYPE.WHOLESALE_INVOICE)
            return 'update-wholesale-invoice';
          if (this.billType === BILL_TYPE.SHIPPING)
            return 'update-delivery-bill';
          return null;
        }
        case 'router-link-id': {
          let base = '#/bills/update-bill';
          if (this.billType === BILL_TYPE.WHOLESALE_INVOICE) {
            base = '#/wholesale-invoice/update-wholesale-invoice';
          } else if (this.billType === BILL_TYPE.SHIPPING) {
            base = '#/delivery-bills/update-delivery-bill';
          }
          return `${base}?id=${item.billNumber}`;
        }
        case 'title': {
          if (this.billType === BILL_TYPE.RETAIL_INVOICE)
            return 'Danh sách hóa đơn bán lẻ';
          if (this.billType === BILL_TYPE.WHOLESALE_INVOICE)
            return 'Danh sách hóa đơn bán B2B';
          if (this.billType === BILL_TYPE.SHIPPING)
            return 'Danh sách hóa đơn giao hàng';
          return '';
        }
        case 'refresh': {
          if (this.billType === BILL_TYPE.RETAIL_INVOICE) return 'list-bill';
          if (this.billType === BILL_TYPE.WHOLESALE_INVOICE)
            return 'list-wholesale-invoice';
          if (this.billType === BILL_TYPE.SHIPPING)
            return 'list-delivery-bill';
          return null;
        }
        case 'convert': {
          return '#/bills/update-bill' + `?id=${item.billNumber}&type=convert`;
        }
      }
    },
    hoverRow: function (evt) {
      const items = jQuery(evt.currentTarget);
      const dataId = jQuery(items[0]).attr('data');
      if (this.hoverTable.dataId !== dataId) {
        const rows = jQuery(`[data=${dataId}]`);
        rows.addClass('tr-rowspan-hover');
        this.hoverTable.dataId = dataId;
      }
    },
    outRow: function (evt) {
      const items = jQuery(evt.currentTarget);
      const dataId = jQuery(items[0]).attr('data');
      const rows = jQuery(`[data=${dataId}]`);
      rows.removeClass('tr-rowspan-hover');
      this.hoverTable.dataId = null;
    },
    hoverBill: function (evt) {
      const items = jQuery(evt.currentTarget);
      const dataId = jQuery(items[0]).attr('data');
      const rows = jQuery(`[data=${dataId}]`);
      rows.addClass('tr-rowspan-hover');
    },
    outBill: function (evt) {
      const items = jQuery(evt.currentTarget);
      const dataId = jQuery(items[0]).attr('data');
      const rows = jQuery(`[data=${dataId}]`);
      rows.removeClass('tr-rowspan-hover');
    },
    onFilter(queryParams) {
      this.$route.query.page = 1;
      this.changeURL();
      this.fetchListBill(queryParams);
    },
    validateParamSearch() {
      const productValidationMessage = validateBillProductSearchInput(
        this.searchProductName,
        this.searchIMEI,
      );
      if (productValidationMessage) {
        makeToastFaile(productValidationMessage);
        return false;
      }
      const textSearchCoupon = this.searchCoupon
        ? this.searchCoupon.trim()
        : '';
      if (textSearchCoupon && textSearchCoupon.length < 5) {
        makeToastFaile('Vui lòng nhập tìm kiếm mã coupon trên 5 ký tự!');
        return false;
      }
      const textSearchNote = this.searchNote ? this.searchNote.trim() : '';
      if (textSearchNote && textSearchNote.length < 5) {
        makeToastFaile('Vui lòng nhập tìm kiếm ghi chú trên 5 ký tự!');
        return false;
      }
      return true;
    },
    changeURL() {
      //let queryObj = this.setFillter();
      const routerName = this.getByType('refresh');
      this.$router.push({
        name: routerName,
        //query: queryObj,
      });
    },
    fetchListBill: async function (queryParams = null) {
      if (queryParams) {
        this.appliedFilterParams = { ...queryParams };
      }
      const filtersToApply =
        queryParams || this.appliedFilterParams || {};
      this.page = this.$route.query.page;
      if (!this.page) {
        this.page = 1;
      }
      const param = {
        page: this.page,
        limit: this.rowsPerPage,
        type: this.billType,
        ...filtersToApply,
      };

      this.onLoading = true;
      ApiService.query('v1/bills', {
        params: param,
      })
        .then((response) => {
          const { data } = response.data;
          this.bills = [];
          this.totalPages = data.totalPage;
          this.totalItems = data.totalRow;
          this.totalQuantity = 0;
          this.totalAmount = 0;
          this.totalPayment = 0;
          let dataset = data.dataset || [];
          if (this.billType === BILL_TYPE.RETAIL_INVOICE) {
            dataset = dataset.filter(
              (row) => row.type === BILL_TYPE.RETAIL_INVOICE,
            );
          }
          this.bills = dataset.reduce((result, item) => {
            let discountAmount = '';
            if (item.discountType === 2) {
              discountAmount = item.discountAmount + '%';
            } else if (item.discountType === 1) {
              discountAmount = convertPrice(item.discountAmount) + '';
            }
            item.isShowReturnBill = item.canReturnBill;
            item.discountAmount = discountAmount;
            item.show = item.listProduct.length <= 0;
            item.showDetail = false;
            const rawLines = item.listProduct || [];
            this.$set(
              item,
              'displayListProduct',
              this.billType === BILL_TYPE.WHOLESALE_INVOICE
                ? groupWholesaleBillListProducts(rawLines, this.billType)
                : rawLines,
            );
            result.push(item);
            return result;
          }, []);
          this.onLoading = false;
        })
        .catch((error) => {
          this.onLoading = false;
          handleError(error);
        });
    },
    isShowByType(type) {
      switch (type) {
        case 'recreate': {
          return this.billType === BILL_TYPE.RETAIL_INVOICE;
        }
        case 'create-warranty': {
          return (
            this.billType === BILL_TYPE.RETAIL_INVOICE ||
            this.billType === BILL_TYPE.SHIPPING
          );
        }
        case 'invoice-vat': {
          return [
            BILL_TYPE.RETAIL_INVOICE,
            BILL_TYPE.WHOLESALE_INVOICE,
            BILL_TYPE.SHIPPING,
          ].includes(this.billType);
        }
        case 'print-bill-invoice': {
          return (
            this.billType === BILL_TYPE.RETAIL_INVOICE ||
            this.billType === BILL_TYPE.SHIPPING
          );
        }
      }
    },
    isShowApprovalModal(extraAmount) {
      // if (!this.checkPermission('APPROVAL_INSERT')) {
      //   return false;
      // }
      return extraAmount > 0;
    },
    editItem: function (item) {
      const { href } = this.$router.resolve({
        name: this.getByType('router-update'),
        query: { id: item.billNumber },
      });
      window.open(href, '_self');
    },
    onClickFilter(filterObject) {
      const queryParams = {
        billNumber: filterObject?.billId || '',
        orderId: filterObject?.orderId || '',
        customerPhoneSearch: filterObject?.customerPhoneSearch || '',
        customerNameSearch: filterObject?.customerNameSearch
          ? String(filterObject.customerNameSearch).trim()
          : '',
        storeIds: filterObject?.storeIds.map((store) => store.id) || [],
        productSearch: filterObject?.productSearch || '',
        imeiSearch: filterObject?.imeiSearch || '',
        searchCoupon: filterObject?.searchCoupon || '',
        employeeId: filterObject?.employeeId || '',
        // BE list lọc theo saleId (= NV kinh doanh / NV bán hàng); employeeId giữ lại cho export
        saleId: filterObject?.employeeId || '',
        criteriaIds:
          filterObject?.criteriaIds.map((criteria) => criteria.id) || [],
        type: this.billType,
        fromDate: filterObject?.fromDate
          ? moment(filterObject.fromDate, 'DD/MM/YYYY').format('yyyy-MM-DD')
          : null,
        toDate: filterObject?.toDate
          ? moment(filterObject.toDate, 'DD/MM/YYYY HH:mm').format(
              'yyyy-MM-DD 23:59',
            )
          : null,
        searchExternalOrderId: filterObject?.externalOrderIdSearch
          ? String(filterObject.externalOrderIdSearch).trim()
          : null,
        orderCodeShip: filterObject?.orderCodeShip
          ? String(filterObject.orderCodeShip).trim()
          : null,
        hasEInvoice: filterObject?.hasEInvoice,
        installmentUnitIds: filterObject?.installmentUnitIds?.map((u) => u.id) || [],
        creditUnitIds: filterObject?.creditUnitIds?.map((u) => u.id) || [],
      };
      this.onFilter(queryParams);
    },
    handleShowNoteModel(item) {
      this.showEditNoteModal = !this.showEditNoteModal;
      this.idBill = item.billNumber;
      this.customerNote = item.customerNote;
      this.warrantyNote = item.warrantyNote;
      this.titleNoteModal = `<div>
              <span class='d-block' style='font-size: 1.1rem'>Sửa ghi chú cho hóa đơn: <a class='text-primary'>#${this.idBill}</a></span>
              </div>`;
    },
    updateNote() {
      const payload = {
        billNumber: this.idBill,
        customerNote: this.customerNote,
        warrantyNote: this.warrantyNote,
      };
      ApiService.put('v1/bills/note', payload)
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.showEditNoteModal = false;
          this.fetchListBill();
        })
        .catch(({ response }) => {
          makeToastFaile(response.$error);
        });
    },
    showReverseExchangeAlert(bill) {
      const relatedNumber = this.getRelatedBillNumber(bill);
      const reason = this.getExchangeReasonName(bill);
      const amountDue =
        bill.exchangeAmountDue != null && bill.exchangeAmountDue > 0
          ? convertPrice(bill.exchangeAmountDue)
          : null;

      let html = `<p>Hóa đơn xuất đổi <strong>#${bill.billNumber}</strong>`;
      if (relatedNumber) {
        html += ` — HĐ gốc <strong>#${relatedNumber}</strong>`;
      }
      html += '.</p>';
      if (reason) {
        html += `<p class="mb-2">Lý do: <strong>${reason}</strong></p>`;
      }
      if (amountDue) {
        html += `<p class="mb-2">Khách đã trả: <strong>${amountDue}</strong></p>`;
      }
      html += `<ul class="text-left small mb-0" style="padding-left:1.2rem">
        <li>SP mới (XKDH) phải còn trạng thái đã bán / đủ tồn nhập lại kho xuất</li>
        <li>SP cũ (NKDH) phải còn NEW tại kho gốc / đủ tồn xuất lại</li>
        <li>Hệ thống sẽ xóa phiếu NKDH + XKDH và hủy thanh toán trên HĐ xuất đổi</li>
      </ul>`;

      Swal.fire({
        title: 'Hoàn xuất đổi',
        html,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Hoàn xuất đổi',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown',
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp',
        },
      }).then((result) => {
        if (result.value) {
          this.deleteItem(bill);
        }
      });
    },
    showDeleteAlert: function (item) {
      Swal.fire({
        title: 'Xóa hóa đơn!',
        text: 'Bạn có chắc muốn xóa hóa đơn này không ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown',
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp',
        },
      }).then((result) => {
        if (result.value) {
          this.deleteItem(item);
        }
      });
    },
    deleteItem: async function (item) {
      ApiService.delete('v1/bills' + '/' + item.billNumber)
        .then((data) => {
          const { message } = data.data;
          makeToastSuccess(message);
          this.fetchListBill();
        })
        .catch((err) => {
          makeToastFaile(err.response.data.message);
        });
    },
    updateFilter(newVal) {
      this.apiParams = {
        billNumber: newVal?.billId || '',
        orderId: newVal?.orderId || '',
        customerPhoneSearch: newVal?.customerPhoneSearch || '',
        customerNameSearch: newVal?.customerNameSearch
          ? newVal.customerNameSearch?.trim()
          : '',
        storeIds: newVal?.storeIds?.map((store) => store.id) || [],
        productSearch: newVal?.productSearch || '',
        imeiSearch: newVal?.imeiSearch || '',
        searchCoupon: newVal?.searchCoupon || '',
        employeeId: newVal?.employeeId || '',
        // BE list lọc theo saleId (= NV kinh doanh / NV bán hàng); employeeId giữ lại cho export
        saleId: newVal?.employeeId || '',
        criteriaIds: newVal?.criteriaIds?.map((criteria) => criteria.id) || [],
        type: this.billType,
        fromDate: newVal?.fromDate
          ? moment(newVal.fromDate, 'DD/MM/YYYY').format('yyyy-MM-DD')
          : null,
        toDate: newVal?.toDate
          ? moment(newVal.toDate, 'DD/MM/YYYY HH:mm').format('yyyy-MM-DD 23:59')
          : null,
        searchExternalOrderId: newVal?.externalOrderIdSearch
          ? String(newVal.externalOrderIdSearch).trim()
          : null,
        orderCodeShip: newVal?.orderCodeShip
          ? String(newVal.orderCodeShip).trim()
          : null,
        hasEInvoice: newVal?.hasEInvoice,
        installmentUnitIds: newVal?.installmentUnitIds?.map((u) => u.id) || [],
        creditUnitIds: newVal?.creditUnitIds?.map((u) => u.id) || [],
      };
    },
    showModalExcel() {
      this.$bvModal.show('modal-bill-excel');
    },
    printBill: async function (item) {
      let routeData = this.$router.resolve({
        name: 'print-bill',
        query: { id: item.billNumber },
      });
      window.open(routeData.href, '_self');
    },
    printBillInvoice: async function (item) {
      let routeData = this.$router.resolve({
        name: 'print-wholesale-invoice',
        query: { id: item.billNumber, mode: 'A4-wholesale-invoice' },
      });
      window.open(routeData.href, '_self');
    },
    printBienBanGiaoHang: async function (item) {
      let routeData = this.$router.resolve({
        name: 'print-bill-documents',
        query: { id: item.billNumber, mode: 'bb-bghh' },
      });
      window.open(routeData.href, '_self');
    },
    printHopDongMuaBan: async function (item) {
      let routeData = this.$router.resolve({
        name: 'print-bill-documents',
        query: { id: item.billNumber, mode: 'hop-dong' },
      });
      window.open(routeData.href, '_self');
    },
    printPhuLucImei: async function (item) {
      let routeData = this.$router.resolve({
        name: 'print-bill-documents',
        query: { id: item.billNumber, mode: 'phu-luc-imei' },
      });
      window.open(routeData.href, '_self');
    },
    editUser(item) {
      this.$router.push({
        name: 'update-customer',
        query: { id: item.customerId, phoneNo: item.customerMobile },
      });
    },
    showApprovalModal(bill, approvalType) {
      const data = {
        amount: this.getBillDisplayExtraAmount(bill),
        customerId: bill.customerId,
        customerName: bill.customerName,
        customerMobile: bill.customerMobile,
        storeId: bill.storeId,
        paymentType: 1,
      };
      this.$refs['popup-approval'].showModal(
        bill.billNumber,
        data,
        approvalType,
      );
    },
    refundBill: async function (item) {
      let routeData = this.$router.resolve({
        name: 'refund-bill',
        query: { relatedBillNumber: item.billNumber },
      });
      window.open(routeData.href, '_self');
    },
    exchangeBill: async function (item) {
      let routeData = this.$router.resolve({
        name: 'exchange-bill',
        query: { relatedBillNumber: item.billNumber },
      });
      window.open(routeData.href, '_self');
    },
    isExchangeBill(bill) {
      return !!(bill?.isExchange || bill?.mode === BILL_MODE.EXCHANGE);
    },
    getBillDisplayExtraAmount(bill) {
      if (this.isExchangeBill(bill) && bill.exchangeAmountDue != null) {
        const amountDue = Number(bill.exchangeAmountDue) || 0;
        const paid = Number(bill.paymentAmount) || 0;
        return paid - amountDue;
      }
      return Number(bill.extraAmount) || 0;
    },
    getBillDisplayLeftAmount(bill) {
      if (this.isExchangeBill(bill) && bill.exchangeAmountDue != null) {
        const extra = this.getBillDisplayExtraAmount(bill);
        return extra < 0 ? Math.abs(extra) : 0;
      }
      return Number(bill.leftAmount) || 0;
    },
    getRelatedBillNumber(bill) {
      return bill?.relatedBillNumber || bill?.returnFromBillNumber || null;
    },
    getExchangeReasonName(bill) {
      if (bill?.exchangeReasonName) {
        return bill.exchangeReasonName;
      }
      const reasonId = bill?.exchangeReasonId;
      if (reasonId && EXCHANGE_REASON_NAME[reasonId]) {
        return EXCHANGE_REASON_NAME[reasonId];
      }
      return '';
    },
    linkToRelatedBill(bill) {
      const relatedNumber = this.getRelatedBillNumber(bill);
      if (!relatedNumber) {
        return;
      }
      const relatedType = bill.returnFromBillType || bill.type;
      let nameRouter = 'update-bill';
      if (relatedType === BILL_TYPE.WHOLESALE_INVOICE) {
        nameRouter = 'update-wholesale-invoice';
      }
      const { href } = this.$router.resolve({
        name: nameRouter,
        query: { id: relatedNumber },
      });
      window.open(href, '_blank');
    },
    shouldShowCreateWarrantyReception(bill) {
      if (bill.type === BILL_TYPE.WHOLESALE_INVOICE) {
        return false;
      }
      const hasProductImei = bill.listProduct.some(({ imeiCode }) => imeiCode);
      return hasProductImei;
    },
    showPrintBillInvoice(bill) {
      return bill.type === BILL_TYPE.WHOLESALE_INVOICE;
    },
    canBootstrapDraftFromFacet(facet) {
      return isFacetBootstrapEligible(facet)
    },
    billRowKey(bill, index, rowKind) {
      const billIdentity = this.resolveBillKeyPart(bill, index)
      return `bill-${billIdentity}-${rowKind}`
    },
    billItemRowKey(bill, billItem, billIndex, itemIndex) {
      const billIdentity = this.resolveBillKeyPart(bill, billIndex)
      const itemIdentity = this.resolveBillItemKeyPart(billItem, itemIndex)
      return `bill-${billIdentity}-item-${itemIndex}-${itemIdentity}`
    },
    resolveBillKeyPart(bill, index) {
      if (bill && bill.id != null) return bill.id
      if (bill && bill.billNumber != null) return bill.billNumber
      return `index-${index}`
    },
    resolveBillItemKeyPart(billItem, index) {
      if (billItem && Array.isArray(billItem._wholesaleMergedDetailIds)) {
        return billItem._wholesaleMergedDetailIds.join('-')
      }
      if (billItem && billItem.id != null) return billItem.id
      if (billItem && billItem.billItemId != null) return billItem.billItemId
      if (billItem && billItem.productId != null) return `product-${billItem.productId}`
      return `index-${index}`
    },
    computeRowspan(bill) {
      const productCount = bill.displayListProduct ? bill.displayListProduct.length : 0
      const shown = Math.min(productCount, this.limitListProduct)
      // +1 for the bill header row, +1 for detail row if shown
      return 1 + shown + (bill.showDetail ? 1 : 0)
    },
    showSelectProductWarranty(bill) {
      this.showSideBar = false;
      this.fetchSalesReceiptForCheckWarranty(bill).then(() => {
        this.showSideBar = true;
      });
      this.showSideBar = false;
    },
    updateBatchBarPadding () {
      const el = this.$el
      if (!el) return
      const hasBar = this.canCreateEInvoiceDraft && this.selectedBills.length > 0
      el.classList.toggle('batch-bar-active', hasBar)
    },
    resolveEInvoiceLaneOrNotify () {
      try {
        return requireEInvoiceLaneByBillType(this.billType)
      } catch (error) {
        makeToastFaile(error?.message || 'Không xác định được lane e-invoice')
        return null
      }
    },
    handleEInvoiceClick (bill) {
      if (!bill?.id) return

      const lane = this.resolveEInvoiceLaneOrNotify()
      if (!lane) return

      const { href } = this.$router.resolve({
        name: 'einvoice-detail',
        params: { salesInvoiceId: bill.id },
        query: { saleType: lane.saleType },
      })

      if (!href) return

      window.open(href, '_blank', 'noopener,noreferrer')
    },
    async refreshSelectedBillFacets() {
      if (!this.selectedBills.length) return []

      const lane = this.resolveEInvoiceLaneOrNotify()
      if (!lane) return []

      try {
        return await this.$store.dispatch(EINVOICE_FETCH_FACETS_BATCH, {
          salesInvoiceIds: this.selectedBills,
          saleType: lane.saleType,
        })
      } catch (_) {
        return []
      }
    },
    buildBillTypeBySalesInvoiceId(bills = []) {
      return bills.reduce((acc, bill) => {
        const salesInvoiceId = bill?.id || bill?.billId || bill?.saleInvoiceId
        if (!salesInvoiceId) {
          return acc
        }

        const resolvedBillType = Number.isInteger(bill?.type) ? bill.type : this.billType
        acc[String(salesInvoiceId)] = resolvedBillType
        return acc
      }, {})
    },
    isBillSelected(id) {
      return this.selectedBills.includes(id)
    },
    onToggleBillSelect(id) {
      const idx = this.selectedBills.indexOf(id)
      if (idx >= 0) {
        this.selectedBills.splice(idx, 1)
      } else {
        this.selectedBills.push(id)
      }
    },
    onToggleSelectAll(checked) {
      if (checked) {
        const toAdd = this.pageBillIds.filter((bn) => !this.selectedBills.includes(bn))
        this.selectedBills.push(...toAdd)
      } else {
        this.selectedBills = this.selectedBills.filter((bn) => !this.pageBillIds.includes(bn))
      }
    },
    clearSelection() {
      this.selectedBills = []
    },
    onRowsPerPageChange() {
      this.page = 1
      if (this.$route.query.page && this.$route.query.page !== '1') {
        this.$router.replace({ query: { ...this.$route.query, page: undefined } })
      }
      this.fetchListBill()
    },
    async openBatchCreateDraftModal() {
      if (!this.canCreateEInvoiceDraft || !this.selectedBills.length) return

      const lane = this.resolveEInvoiceLaneOrNotify()
      if (!lane) return

      await this.refreshSelectedBillFacets()

      if (
        this.selectedBills.length === 1 &&
        this.selectedDraftableBills.length === 0 &&
        this.selectedOpenableBills.length === 1
      ) {
        this.$router.push({
          name: 'einvoice-detail',
          params: { salesInvoiceId: this.selectedOpenableBills[0].id },
          query: { saleType: lane.saleType },
        })
        return
      }

      if (!this.selectedDraftableBills.length) {
        makeToastFaile('Không có bill nào còn ở trạng thái cho phép tạo draft GTGT.')
        return
      }

      const skippedCount = this.selectedBills.length - this.selectedDraftableBills.length
      if (skippedCount > 0) {
        makeToastWarning(
          `Chỉ ${this.selectedDraftableBills.length}/${this.selectedBills.length} hóa đơn sẽ đi vào flow tạo nháp. ${skippedCount} hóa đơn còn lại đã có HĐĐT hoặc không còn ở lane tạo nháp.`,
        )
      }

      this.$refs.batchCreateDraftModal.open({
        billIds: this.selectedDraftableBills.map((bill) => bill.id),
        providerMode: lane.providerMode,
        billTypeBySalesInvoiceId: this.buildBillTypeBySalesInvoiceId(this.selectedDraftableBills),
        entryPoint: 'BILL_LIST',
        bills: this.selectedDraftableBills,
      })
    },
    onBatchDraftFinished(summary) {
      this.selectedBills = []
      if (summary.readyCount > 0 || summary.needsInfoCount > 0) {
        this.fetchListBill()
        this.refreshSelectedBillFacets()
      }
    },
    exportDanhSachImeiExcel: async function (item) {
      const billNumber = item.billNumber;
      try {
        const response = await ApiService.get('v1/bills/export-document', {
          params: { billNumber, mode: 'danh-sach-imei' },
          responseType: 'blob',
        });
        const fileName = fileDownload.getFileName(response) || `danh-sach-imei-${billNumber}.xlsx`;
        fileDownload.createFile(response.data, fileName);
        makeToastSuccess('Tải file Excel danh sách IMEI thành công!');
      } catch (err) {
        try {
          const text = await err.response?.data?.text();
          const json = JSON.parse(text);
          err.response.data = json;
        } catch (_) {}
        handleError(err);
      }
    },
  },
};
</script>

<style scoped>
.orderCode {
  text-align: center;
}
.orderCode:hover {
  text-decoration: underline;
}

.table th,
.table td {
  vertical-align: middle;
}

.tdTextAlign {
  vertical-align: middle;
}

.tdTextAlignPrice {
  text-align: right !important;
  vertical-align: middle;
}

.table-bordered-list {
  table-layout: fixed;
  width: 100%;
}

.table-bordered-list th {
  border: 0.5px solid #d2d4d6 !important;
  background-color: #e0e0e0;
}

.table-bordered-list td {
  border: 0.5px solid #f3f3f3;
  /* border: 0.5px solid #fdfbfb; */
  /* border: 0.5px solid #d5d5d5; */
  border-bottom: 0.5px solid #adadad;
}

.text-weight {
  font-weight: 550;
}

.font-size-gift {
  font-size: 11px;
}

.font-size-attach {
  font-size: 11px;
}

.tdCode {
  width: 8%;
}

.tdCustomer {
  width: 7%;
  max-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.tdCustomer p {
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.tdProductName {
  width: 34%;
}

.tdProductName.wholesale {
  width: 19%;
}

.bill-list-imei-text {
  display: inline-block;
  max-width: 100%;
  word-break: break-word;
  font-size: 0.8125rem;
}

.list-bill--wholesale {
  font-size: 0.8125rem;

  .table-bordered-list th,
  .table-bordered-list td {
    font-size: 0.8125rem;
  }

  .bill-list-imei-text {
    font-size: 0.75rem;
  }

  .font-size-gift,
  .font-size-attach {
    font-size: 10px;
  }
}

.tdProductPrice {
  width: 8%;
}

.tdQuantity {
  width: 3%;
}

.tdDiscountItem {
  width: 7%;
}

.tdTotalAmount {
  width: 8%;
}

.tdTotalPayment {
  width: 12%;
  font-weight: 700;
}

.tdNote {
  width: 10%;
}

.tdAction {
  width: 40px;
  padding: 2px 0 !important;
  white-space: nowrap;
  text-align: center;
}

.tdAction.wholesale {
  width: 5%;
  padding: 2px 0 !important;
  white-space: nowrap;
  text-align: center;
}

.search-col {
  display: flex;
  align-items: center;
}



/* Bill List Toolbar */
.bill-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  margin-bottom: 4px;
}

.bill-list-toolbar__left {
  display: flex;
  align-items: center;
}

.bill-list-toolbar__total {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.bill-list-toolbar__total-label {
  font-size: 13px;
  font-weight: 450;
  color: #64748b;
}

.bill-list-toolbar__total-value {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
}

.bill-list-toolbar__right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.bill-list-toolbar__divider {
  width: 1px;
  height: 20px;
  background: #e2e8f0;
}

.bill-list-pagination {
  margin-bottom: 0 !important;
}

.bill-list-pagination .ul {
  margin-bottom: 0 !important;
}

.bill-list-pagination .page-link {
  border-radius: 6px !important;
}
</style>

<style lang="scss" scoped>
.bill-page ::v-deep {
  .select-style {
    border-radius: 0.28rem;
    box-shadow: none;
    border: 1px solid #ced4da;
  }

  input.form-control {
    border: 1px solid #ced4da;
  }

  .dashed {
    border: 1px dashed #464e5f;
    border-style: none none dashed;
    color: #fff;
    background-color: #fff;
  }

  .filter-btn {
    font-weight: 600;
    width: 70px;
  }

  .function-btn {
    font-size: 1rem;
    padding-right: 0;
  }

  .edit-note-btn {
    font-weight: 600;
    width: 70px;
  }

  .bill-header-meta {
    font-size: 0.87rem;
  }

  .customer-header-info {
    margin-bottom: 2px;
    font-size: 0.87rem;
    line-height: 1.35;

    > small.text-muted {
      margin-right: 4px;
    }
  }

  .customer-header-info__values {
    .customer-name {
      margin-right: 6px;
    }
  }

  .customer-header-info .customer-name {
    font-size: 0.87rem;
    font-weight: 700;
    color: #3699ff;
  }

  .customer-header-info .customer-mobile {
    font-size: 0.87rem;
    font-weight: 600;
    color: #464e5f;
  }

  .product-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .product-codes {
    font-size: 12px;
    color: #475569; /* xám nhẹ */
    font-weight: 500;
  }

  .code-text {
    margin-right: 4px;
  }

  .product-title {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
    color: #111827;
  }

  .product-icon {
    margin-right: 6px;
    font-size: 14px;
  }
  .product-icon.gift {
    color: #2e7d32;
  }
  .product-icon.attach {
    color: #37474f;
  }

  .product-name {
    font-size: 14px;
  }

  .product-meta {
    margin-left: 2px; /* canh với icon ở trên */
    margin-top: 2px;
  }

  .list-bill--wholesale {
    .bill-header-meta {
      font-size: 0.8rem;
    }

    .customer-header-info {
      font-size: 0.8rem;

      .customer-name,
      .customer-mobile {
        font-size: 0.8rem;
      }
    }
  }

  .tdProductName.wholesale {
    width: 38%;
    .product-codes { font-size: 9px; }
    .product-title { font-size: 11px; }
    .product-name { font-size: 11px; }
    .product-icon { font-size: 11px; }
  }



  .tdNote.wholesale {
    width: 8%;
    font-size: 0.8125rem;
  }

  .tdAction.wholesale {
    width: 5%;
    padding: 2px 0 !important;
    overflow: visible;
    text-align: center;

    .dropdown-toggle.btn,
    .dropdown-toggle.btn-sm {
      margin-left: 10px;
      margin-right: 10px;
    }
  }
}

.bill-row-selected {
  background-color: #e8f4fd !important;
}

.bill-row-selected td {
  background-color: #e8f4fd !important;
}

.bill-select-cell {
  vertical-align: top;
  padding-top: 12px !important;
}

/* ── Floating Batch Action Bar ───────────────────────────── */
.batch-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1030;
  background: #fff;
  border-top: 1px solid #e4e6ef;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.batch-bar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 24px;
  gap: 16px;
}

.batch-bar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.batch-bar-count-text {
  font-size: 0.875rem;
  color: #464e5f;
  white-space: nowrap;

  strong {
    color: #3699ff;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.batch-bar-warning {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f1a22b;
  font-size: 0.875rem;
  cursor: help;

  &:hover {
    color: #d68f1a;
  }
}

.batch-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.batch-bar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid #3699ff;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--clear {
    background: #f3f6f9;
    color: #5e6278;

    &:hover:not(:disabled) {
      background: #e9ecf0;
      color: #3f4254;
    }
  }

  &--primary {
    background: #3699ff;
    color: #fff;

    &:hover:not(:disabled) {
      background: #187de4;
    }

    &:disabled {
      background: #b5b5c3;
    }
  }
}

/* ── Slide Transition ────────────────────────────────────── */
.batch-bar-slide-enter-active,
.batch-bar-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.2s ease;
}

.batch-bar-slide-enter,
.batch-bar-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.batch-bar-slide-enter-to,
.batch-bar-slide-leave {
  transform: translateY(0);
  opacity: 1;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 767px) {
  .batch-bar-inner {
    padding: 10px 16px;
    gap: 12px;
  }

  .batch-bar-count-text {
    font-size: 0.8125rem;
  }

  .batch-bar-btn {
    padding: 8px 12px;
    font-size: 0.75rem;
  }

  .batch-bar-btn--clear {
    padding: 8px 10px;
  }
}

/* ── Bottom padding when bar is active ───────────────────── */
.bill-page {
  padding-bottom: 0;
  transition: padding-bottom 0.25s ease;

  &.batch-bar-active {
    padding-bottom: 60px;
  }
}

.bill-row-selected {
  background-color: #e8f4fd !important;
}

.bill-row-selected td {
  background-color: #e8f4fd !important;
}

.bill-select-cell {
  vertical-align: top;
  padding-top: 12px !important;
}

/* ── Floating Batch Action Bar ───────────────────────────── */
.batch-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1030;
  background: #fff;
  border-top: 1px solid #e4e6ef;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.batch-bar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 24px;
  gap: 16px;
}

.batch-bar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.batch-bar-count-text {
  font-size: 0.875rem;
  color: #464e5f;
  white-space: nowrap;

  strong {
    color: #3699ff;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.batch-bar-warning {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f1a22b;
  font-size: 0.875rem;
  cursor: help;

  &:hover {
    color: #d68f1a;
  }
}

.batch-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.batch-bar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid #3699ff;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--clear {
    background: #f3f6f9;
    color: #5e6278;

    &:hover:not(:disabled) {
      background: #e9ecf0;
      color: #3f4254;
    }
  }

  &--primary {
    background: #3699ff;
    color: #fff;

    &:hover:not(:disabled) {
      background: #187de4;
    }

    &:disabled {
      background: #b5b5c3;
    }
  }
}

/* ── Slide Transition ────────────────────────────────────── */
.batch-bar-slide-enter-active,
.batch-bar-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.2s ease;
}

.batch-bar-slide-enter,
.batch-bar-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.batch-bar-slide-enter-to,
.batch-bar-slide-leave {
  transform: translateY(0);
  opacity: 1;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 767px) {
  .batch-bar-inner {
    padding: 10px 16px;
    gap: 12px;
  }

  .batch-bar-count-text {
    font-size: 0.8125rem;
  }

  .batch-bar-btn {
    padding: 8px 12px;
    font-size: 0.75rem;
  }

  .batch-bar-btn--clear {
    padding: 8px 10px;
  }
}

/* ── Bottom padding when bar is active ───────────────────── */
.bill-page {
  padding-bottom: 0;
  transition: padding-bottom 0.25s ease;

  &.batch-bar-active {
    padding-bottom: 60px;
  }
}
</style>
